import './index.pcss';

import Uploader from './uploader';

const IconFile = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" style="fill: none" xmlns="http://www.w3.org/2000/svg"><path d="M13.32 8.44 9.5 12.19c-.37.36-.57.85-.56 1.37a2.05 2.05 0 0 0 2 1.98c.53 0 1.02-.19 1.39-.55l3.83-3.75a3.75 3.75 0 0 0 1.1-2.74 4.1 4.1 0 0 0-4-3.96 3.85 3.85 0 0 0-2.77 1.1L6.66 9.39A5.63 5.63 0 0 0 5 13.5a6.14 6.14 0 0 0 6 5.94 5.77 5.77 0 0 0 4.17-1.64l3.83-3.75" stroke="#000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const DownloadIcon = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="m7 10 4.86 4.86c.08.08.2.08.28 0L17 10" stroke="#000" stroke-width="2" stroke-linecap="round"/></svg>';
import {make, moveCaretToTheEnd, isEmpty} from './utils/dom';
import {getExtensionFromFileName} from './utils/file';

const LOADER_TIMEOUT = 500;

/**
 * @typedef {object} AttachesToolData
 * @description Attaches Tool's output data format
 * @property {AttachesFileData} file - object containing information about the file
 * @property {string} title - file's title
 */

/**
 * @typedef {object} AttachesFileData
 * @description Attaches Tool's file format
 * @property {string} [url] - file's upload url
 * @property {string} [size] - file's size
 * @property {string} [extension] - file's extension
 * @property {string} [name] - file's name
 */

/**
 * @typedef {object} FileData
 * @description Attaches Tool's response from backend. Could contain any data.
 * @property {string} [url] - file's url
 * @property {string} [name] - file's name with extension
 * @property {string} [extension] - file's extension
 */

/**
 * @typedef {object} UploadResponseFormat
 * @description This format expected from backend on file upload
 * @property {number} success  - 1 for successful uploading, 0 for failure
 * @property {FileData} file - backend response with uploaded file data.
 */

/**
 * @typedef {object} AttachesToolConfig
 * @description Config supported by Tool
 * @property {string} endpoint - file upload url
 * @property {string} field - field name for uploaded file
 * @property {string} types - available mime-types
 * @property {string} errorMessage - message to show if file uploading failed
 * @property {object} [uploader] - optional custom uploader
 * @property {function(File): Promise.<UploadResponseFormat>} [uploader.uploadByFile] - custom method that upload file and returns response
 */

/**
 * @typedef {object} EditorAPI
 * @property {object} styles - Styles API {@link https://github.com/codex-team/editor.js/blob/next/types/api/styles.d.ts}
 * @property {object} i18n - Internationalization API {@link https://github.com/codex-team/editor.js/blob/next/types/api/i18n.d.ts}
 * @property {object} notifier - Notifier API {@link https://github.com/codex-team/editor.js/blob/next/types/api/notifier.d.ts}
 */

/**
 * @class AttachesTool
 * @classdesc AttachesTool for Editor.js 2.0
 */
export default class AttachesTool {
    /**
     * @param {object} options - tool constructor options
     * @param {AttachesToolData} [options.data] - previously saved data
     * @param {AttachesToolConfig} options.config - user defined config
     * @param {EditorAPI} options.api - Editor.js API
     * @param {boolean} options.readOnly - flag indicates whether the Read-Only mode enabled or not
     */
    constructor({data, config, api, readOnly}) {
        this.api = api;
        this.readOnly = readOnly;

        this.nodes = {
            wrapper: null,
            button: null,
            title: null,
        };

        this._data = {
            file: {},
            title: '',
        };

        this.config = {
            endpoint: config.endpoint || '',
            field: config.field || 'file',
            types: config.types || '*',
            buttonText: config.buttonText || 'Select file to upload',
            errorMessage: config.errorMessage || 'File upload failed',
            uploader: config.uploader || undefined,
            additionalRequestHeaders: config.additionalRequestHeaders || {},
        };

        if (data !== undefined && !isEmpty(data)) {
            this.data = data;
        }

        /**
         * Module for files uploading
         */
        this.uploader = new Uploader({
            config: this.config,
            onUpload: (response) => this.onUpload(response),
            onError: (error) => this.uploadingFailed(error),
        });

        this.enableFileUpload = this.enableFileUpload.bind(this);
    }

    /**
     * Get Tool toolbox settings
     * icon - Tool icon's SVG
     * title - title to show in toolbox
     *
     * @returns {{icon: string, title: string}}
     */
    static get toolbox() {
        return {
            icon: IconFile,
            title: 'Attachment',
        };
    }

    /**
     * Returns true to notify core that read-only is supported
     *
     * @returns {boolean}
     */
    static get isReadOnlySupported() {
        return true;
    }

    /**
     * Tool's CSS classes
     *
     * @returns {object}
     */
    get CSS() {
        return {
            baseClass: this.api.styles.block,
            apiButton: this.api.styles.button,
            loader: this.api.styles.loader,
            /**
             * Tool's classes
             */
            wrapper: 'cdx-attaches',
            wrapperWithFile: 'cdx-attaches--with-file',
            wrapperLoading: 'cdx-attaches--loading',
            button: 'cdx-attaches__button',
            title: 'cdx-attaches__title',
            size: 'cdx-attaches__size',
            downloadButton: 'cdx-attaches__download-button',
            fileInfo: 'cdx-attaches__file-info',
            fileIcon: 'cdx-attaches__file-icon',
            fileIconBackground: 'cdx-attaches__file-icon-background',
            fileIconLabel: 'cdx-attaches__file-icon-label',
        };
    }

    /**
     * Possible files' extension colors
     *
     * @returns {object}
     */
    get EXTENSIONS() {
        return {
            doc: '#1483E9',
            docx: '#1483E9',
            odt: '#1483E9',
            pdf: '#DB2F2F',
            rtf: '#744FDC',
            tex: '#5a5a5b',
            txt: '#5a5a5b',
            pptx: '#E35200',
            ppt: '#E35200',
            mp3: '#eab456',
            mp4: '#f676a6',
            xls: '#11AE3D',
            html: '#2988f0',
            htm: '#2988f0',
            png: '#AA2284',
            jpg: '#D13359',
            jpeg: '#D13359',
            gif: '#f6af76',
            zip: '#4f566f',
            rar: '#4f566f',
            exe: '#e26f6f',
            svg: '#bf5252',
            key: '#00B2FF',
            sketch: '#FFC700',
            ai: '#FB601D',
            psd: '#388ae5',
            dmg: '#e26f6f',
            json: '#2988f0',
            csv: '#11AE3D',
        };
    }

    /**
     * Validate block data:
     * - check for emptiness
     *
     * @param {AttachesToolData} savedData — data received after saving
     * @returns {boolean} false if saved data is not correct, otherwise true
     * @public
     */
    validate(savedData) {
        if (isEmpty(savedData.file)) {
            return false;
        }

        return true;
    }

    /**
     * Return Block data
     *
     * @param {HTMLElement} toolsContent - block main element returned by the render method
     * @returns {AttachesToolData}
     */
    save(toolsContent) {
        /**
         * If file was uploaded
         */
        if (this.pluginHasData()) {
            const titleElement = toolsContent.querySelector(`.${this.CSS.title}`);

            if (titleElement) {
                Object.assign(this.data, {
                    title: titleElement.innerHTML,
                });
            }
        }

        return this.data;
    }

    /**
     * Renders Block content
     *
     * @returns {HTMLDivElement}
     */
    render() {
        const holder = make('div', this.CSS.baseClass);

        this.nodes.wrapper = make('div', this.CSS.wrapper);

        if (this.pluginHasData()) {
            this.showFileData();
        } else {
            this.prepareUploadButton();
        }

        holder.appendChild(this.nodes.wrapper);

        return holder;
    }

    /**
     * Prepares button for file uploading
     */
    prepareUploadButton() {
        this.nodes.button = make('div', [this.CSS.apiButton, this.CSS.button]);
        this.nodes.button.innerHTML = `${IconFile} ${this.config.buttonText}`;

        if (!this.readOnly) {
            this.nodes.button.addEventListener('click', this.enableFileUpload);
        }

        this.nodes.wrapper.appendChild(this.nodes.button);
    }

    /**
     * Fires after clicks on the Toolbox AttachesTool Icon
     * Initiates click on the Select File button
     *
     * @public
     */
    appendCallback() {
        this.nodes.button.click();
    }

    /**
     * Checks if any of Tool's fields have data
     *
     * @returns {boolean}
     */
    pluginHasData() {
        return this.data.title !== '' || Object.values(this.data.file).some(item => item !== undefined);
    }

    /**
     * Allow to upload files on button click
     */
    enableFileUpload() {
        this.uploader.uploadSelectedFile({
            onPreview: () => {
                this.nodes.wrapper.classList.add(this.CSS.wrapperLoading, this.CSS.loader);
            },
        });
    }

    /**
     * File uploading callback
     *
     * @param {UploadResponseFormat} response - server returned data
     */
    onUpload(response) {
        const body = response;


        try {
            if (body.success && body.file !== undefined && !isEmpty(body.file)) {
                this.data = {
                    file: body.file,
                    title: body.file.title || '',
                };

                this.nodes.button.remove();
                this.showFileData();

                moveCaretToTheEnd(this.nodes.title);

                this.removeLoader();
            } else {
                this.uploadingFailed(this.config.errorMessage);
            }
        } catch (error) {
            console.error('Attaches tool error:', error);
            this.uploadingFailed(this.config.errorMessage);
        }
    }

    /**
     * Handles uploaded file's extension and appends corresponding icon
     *
     * @param {object<string, string | number | boolean>} file - uploaded file data got from the backend. Could contain any fields.
     */
    appendFileIcon(file) {
        const extensionProvided = file.extension;
        const extension = extensionProvided || getExtensionFromFileName(file.name);
        const extensionColor = this.EXTENSIONS[extension];
        const extensionMaxLen = 4;

        const wrapper = make('div', this.CSS.fileIcon);
        const background = make('div', this.CSS.fileIconBackground);

        if (extensionColor) {
            background.style.backgroundColor = extensionColor;
        }

        wrapper.appendChild(background);

        /**
         * If extension exists, add it via a separate element
         * Otherwise, append file icon
         */
        if (extension) {
            /**
             * Trim long extensions
             *  'sketch' -> 'sket…'
             */
            let extensionVisible = extension;

            if (extension.length > extensionMaxLen) {
                extensionVisible = extension.substring(0, extensionMaxLen) + '…';
            }

            const extensionLabel = make('div', this.CSS.fileIconLabel, {
                textContent: extensionVisible, // trimmed
                title: extension, // full text for hover
            });

            if (extensionColor) {
                extensionLabel.style.backgroundColor = extensionColor;
            }

            wrapper.appendChild(extensionLabel);
        } else {
            background.innerHTML = IconFile;
        }

        this.nodes.wrapper.appendChild(wrapper);
    }

    /**
     * Removes tool's loader
     */
    removeLoader() {
        setTimeout(() => this.nodes.wrapper.classList.remove(this.CSS.wrapperLoading, this.CSS.loader), LOADER_TIMEOUT);
    }

    /**
     * If upload is successful, show info about the file
     */
    showFileData() {
        this.nodes.wrapper.classList.add(this.CSS.wrapperWithFile);

        const {file, title} = this.data;

        this.appendFileIcon(file);

        const fileInfo = make('div', this.CSS.fileInfo);

        this.nodes.title = make('div', this.CSS.title, {
            contentEditable: this.readOnly === false,
        });

        this.nodes.title.dataset.placeholder = this.api.i18n.t('File title');
        this.nodes.title.textContent = title || '';
        fileInfo.appendChild(this.nodes.title);

        if (file.size) {
            let sizePrefix;
            let formattedSize;
            const fileSize = make('div', this.CSS.size);

            if (Math.log10(+file.size) >= 6) {
                sizePrefix = 'MiB';
                formattedSize = file.size / Math.pow(2, 20);
            } else {
                sizePrefix = 'KiB';
                formattedSize = file.size / Math.pow(2, 10);
            }

            fileSize.textContent = formattedSize.toFixed(1);
            fileSize.setAttribute('data-size', sizePrefix);
            fileInfo.appendChild(fileSize);
        }

        this.nodes.wrapper.appendChild(fileInfo);

        if (file.url !== undefined) {
            const downloadIcon = make('a', this.CSS.downloadButton, {
                innerHTML: DownloadIcon,
                href: file.url,
                target: '_blank',
                rel: 'nofollow noindex noreferrer',
            });

            this.nodes.wrapper.appendChild(downloadIcon);
        }
    }

    /**
     * If file uploading failed, remove loader and show notification
     *
     * @param {string} errorMessage -  error message
     */
    uploadingFailed(errorMessage) {
        this.api.notifier.show({
            message: errorMessage,
            style: 'error',
        });

        this.removeLoader();
    }

    /**
     * Return Attaches Tool's data
     *
     * @returns {AttachesToolData}
     */
    get data() {
        return this._data;
    }

    /**
     * Stores all Tool's data
     *
     * @param {AttachesToolData} data - data to set
     */
    set data({file, title}) {
        this._data = {
            file,
            title,
        };
    }
}
